import { useState } from 'react';
import { getCategoriesOptions } from '@generated';
import { SearchInput } from '~/shared/forms/controls/base-controls/text-controls/SearchInput';
import { Section } from '~/shared/section/Section';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useGetCategoryLabel } from '~/libraries/categories/useGetCategoryLabel';
import { GoalCard } from '../../goals/GoalCard';
import { useGoalsAndInterventionsWizard } from '../GoalsAndInterventionsWizardContext';
import { DataTablePager } from '~/shared/data-tables/DataTablePager';
import { ButtonGroup } from '~/shared/forms/controls/base-controls/ButtonGroup';

function filterGoals(
	goalDefinitions: { id: string; title: string; definitionText: string; category: string }[],
	existingGoalDefinitionIds: string[],
	selectedCategoryNames: string[],
	searchText: string
): string[] {
	return goalDefinitions
		.filter(goalDefinition => {
			if (existingGoalDefinitionIds.some(x => x === goalDefinition.id)) {
				return false;
			} else if (
				searchText.length >= 3 &&
				!goalDefinition.title.toLowerCase().includes(searchText.toLowerCase()) &&
				!goalDefinition.definitionText.toLowerCase().includes(searchText.toLowerCase())
			) {
				return false;
			} else if (selectedCategoryNames.length > 0 && !selectedCategoryNames.includes(goalDefinition.category)) {
				return false;
			}
			return true;
		})
		.map(x => x.id);
}

export function NewGoalSearch() {
	const { wizardState, existingGoals, goalAndInterventionDefinitionLibrary, dispatch, dispatchPredefinedGoalSelected } =
		useGoalsAndInterventionsWizard();
	const [searchTerm, setSearchTerm] = useState(wizardState.selectedGoalSearchTerm);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(3);

	const { data: categories } = useSuspenseQuery(getCategoriesOptions());
	const getCategoryLabel = useGetCategoryLabel();

	const existingGoalDefinitionIds = existingGoals.map(x => x.goalDefinitionId).filter(x => x !== null) as string[];
	const filteredGoalIds = filterGoals(
		goalAndInterventionDefinitionLibrary.goalDefinitions,
		existingGoalDefinitionIds,
		wizardState.selectedGoalSelectedCategories,
		searchTerm
	);
	const filteredGoals = goalAndInterventionDefinitionLibrary.goalDefinitions
		.filter(x => filteredGoalIds.includes(x.id))
		.toSorted((x, y) => x.title.toLocaleLowerCase().localeCompare(y.title.toLocaleLowerCase()));
	const totalPages = Math.max(1, Math.ceil(filteredGoals.length / pageSize));
	const page = Math.min(currentPage, totalPages);
	const pagedGoals = filteredGoals.slice((page - 1) * pageSize, page * pageSize);
	const goalCategoryNames = new Set([...goalAndInterventionDefinitionLibrary.goalDefinitions.map(x => x.category)]);
	const goalCategories = categories.filter(x => goalCategoryNames.has(x.name));

	const onPredefinedGoalSelect = (id: string) => {
		if (id === 'custom') {
			dispatch({ type: 'CUSTOM_GOAL_SELECTED', newGoalId: window.crypto.randomUUID() });
			return;
		}
		const goalDefinition = goalAndInterventionDefinitionLibrary.goalDefinitions.find(x => x.id === id);
		if (!goalDefinition) {
			throw new Error('Cannot find goal definition');
		}
		dispatchPredefinedGoalSelected(goalDefinition);
	};

	return (
		<div className="flex flex-col space-y-16 py-2">
			<div className="flex flex-col space-y-6">
				<Section>
					<div className="flex flex-col gap-4">
						<SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search goals..." />
						{goalCategories.length > 0 && (
							<ButtonGroup
								options={goalCategories.map(x => ({ id: x.name, label: getCategoryLabel(x.name) }))}
								isSelected={(id: string) => wizardState.selectedGoalSelectedCategories.includes(id)}
								onSelect={(id: string) => dispatch({ type: 'GOAL_CATEGORY_TOGGLED', category: id })}
								variant="chips"
							/>
						)}
					</div>
				</Section>
				<div className="flex flex-col space-y-3">
					{searchTerm.length > 3 && filteredGoals.length === 0 ? (
						<div className="text-center text-sm text-gray-500">No goals found</div>
					) : (
						<>
							{filteredGoals.length > 0 && (
								<DataTablePager
									currentPage={page}
									totalPages={totalPages}
									totalCount={filteredGoals.length}
									pageSize={pageSize}
									pageSizeOptions={[3, 5, 10]}
									onPageChange={setCurrentPage}
									onPageSizeChange={size => {
										setPageSize(size);
										setCurrentPage(1);
									}}
								/>
							)}
							{pagedGoals.map(x => (
								<GoalCard
									key={x.id}
									title={x.title}
									definitionText={x.definitionText}
									onGoalSelect={() => onPredefinedGoalSelect(x.id)}
								/>
							))}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
